// ./src/components/generic/form/MultiSelectField.tsx
//
// MultiSelect on the shared `(name, value)` change contract — emits the chosen values (an empty array when none).
//
// ⚠ ON A PHONE IT IS ALWAYS THE FULL-SCREEN PICKER (OptionPickerModal in `multiple` mode: rows toggle, Done closes),
//   for the same reason as `SelectField`: Mantine's inline dropdown inside a bottom-sheet drawer resized the sheet as it
//   opened and as the keyboard came up. The trigger shows the chosen options as pills, each removable in place.
//   Desktop is always Mantine's MultiSelect.

import React, { useMemo, useState } from 'react';
import { extractStyleProps, MultiSelect, MultiSelectProps, Pill, PillsInput } from '@mantine/core';

import { useAppEnvironment } from '../AppEnvironmentContext';
import { AppIcon } from '../AppIcon';

import { FieldChangeHandler } from './types';
import { OptionPickerModal } from './OptionPickerModal';
import { phonePickerSearchable, usesPhonePicker } from './pickerRules';

export interface MultiSelectFieldProps extends Omit<MultiSelectProps, 'onChange' | 'value'> {
  name?: string;
  value?: string[];
  onChange?: FieldChangeHandler<string[]>;
  /** The phone picker's surface style (the public event site passes its theme, `usePortalSurfaceStyle`). */
  modalContentStyle?: React.CSSProperties;
  /** Label of the phone picker's Done button. */
  doneLabel?: string;
}

/** value → label over Mantine's data shapes (string[], {value,label}[], grouped {group, items}[]). */
export const labelMap = (data: unknown): Map<string, string> => {
  const map = new Map<string, string>();
  const scan = (items: unknown[]) => {
    for (const it of items) {
      if (typeof it === 'string' || typeof it === 'number') {
        map.set(String(it), String(it));
      } else if (it && typeof it === 'object') {
        const o = it as { value?: unknown; label?: unknown; items?: unknown[] };
        if (o.items) {
          scan(o.items);
        } else if (o.value != null) {
          map.set(String(o.value), String(o.label ?? o.value));
        }
      }
    }
  };

  if (Array.isArray(data)) {
    scan(data);
  }

  return map;
};

/**
 * The multi pickers' phone TRIGGER: an input box holding the chosen values as pills (each removable in place) that
 * opens the full-screen picker. It keeps the caller's layout props (`mt`, `w`, `flex`, …).
 */
export const PillsPickerTrigger: React.FC<{
  fieldProps: Omit<MultiSelectProps, 'onChange' | 'value'>;
  values: string[];
  labels: Map<string, string>;
  onRemove: (value: string) => void;
  onOpen: () => void;
}> = ({ fieldProps, values, labels, onRemove, onOpen }) => {
  // The caller's OBJECT styles / classNames carry over (a function is typed for a MultiSelect) — NetworkingCard themes
  // its label and text through them, and lost both on a phone (#138 review).
  const callerStyles = fieldProps.styles && typeof fieldProps.styles === 'object' ? (fieldProps.styles as Record<string, React.CSSProperties>) : {};
  const callerClassNames = fieldProps.classNames && typeof fieldProps.classNames === 'object' ? (fieldProps.classNames as Record<string, string>) : undefined;
  const ariaLabel = (fieldProps as { 'aria-label'?: string })['aria-label'] ?? (typeof fieldProps.label === 'string' ? fieldProps.label : undefined);

  return (
  <PillsInput
    // The wrapper's id becomes the field's, so the label is tied to it (Input.Wrapper hands it to PillsInput.Field).
    id={fieldProps.id}
    label={fieldProps.label}
    description={fieldProps.description}
    error={fieldProps.error}
    required={fieldProps.required}
    withAsterisk={fieldProps.withAsterisk}
    size={fieldProps.size}
    leftSection={fieldProps.leftSection}
    rightSection={<AppIcon iconName="ChevronDown" size={18} />}
    rightSectionPointerEvents="none"
    // Every layout prop the caller gave the select (`mt`, `w`, `flex`, …) — dropping them moved fields about.
    {...extractStyleProps(fieldProps).styleProps}
    style={fieldProps.style}
    className={fieldProps.className}
    onClick={onOpen}
    classNames={callerClassNames}
    styles={{ ...callerStyles, input: { ...(callerStyles.input ?? {}), cursor: 'pointer' } }}
  >
    <Pill.Group>
      {values.map((v) => (
        <Pill
          key={v}
          size="md"
          withRemoveButton
          onRemove={() => onRemove(v)}
          // A tap on the pill's ✕ removes it; it must not open the picker as well.
          onClick={(e) => e.stopPropagation()}
        >
          {labels.get(v) ?? v}
        </Pill>
      ))}
      <PillsInput.Field
        readOnly
        placeholder={values.length === 0 && typeof fieldProps.placeholder === 'string' ? fieldProps.placeholder : undefined}
        aria-label={ariaLabel}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            onOpen();
          }
        }}
        style={{ caretColor: 'transparent', cursor: 'pointer', ...(callerStyles.inputField ?? {}) }}
      />
    </Pill.Group>
  </PillsInput>
  );
};

export const MultiSelectField: React.FC<MultiSelectFieldProps> = ({ name, value, onChange, modalContentStyle, doneLabel, ...rest }) => {
  const { isMobile } = useAppEnvironment();
  const [pickerOpen, setPickerOpen] = useState(false);
  const values = useMemo(() => value ?? [], [value]);
  const labels = useMemo(() => labelMap(rest.data), [rest.data]);
  const optionCount = labels.size;

  if (usesPhonePicker({ isMobile, disabled: rest.disabled, readOnly: rest.readOnly })) {
    // `maxValues` holds on a phone too: a row past the limit does not add (removing one is always allowed).
    const full = rest.maxValues != null && values.length >= rest.maxValues;
    const toggle = (v: string) => {
      if (values.includes(v)) {
        onChange?.(name, values.filter((x) => x !== v));
      } else if (!full) {
        onChange?.(name, [...values, v]);
      }
    };

    return (
      <>
        <PillsPickerTrigger
          fieldProps={rest}
          values={values}
          labels={labels}
          onRemove={(v) => onChange?.(name, values.filter((x) => x !== v))}
          onOpen={() => setPickerOpen(true)}
        />

        <OptionPickerModal
          opened={pickerOpen}
          onClose={() => setPickerOpen(false)}
          data={rest.data}
          multiple
          values={values}
          onToggle={toggle}
          title={rest.label ?? rest.placeholder ?? (rest as { 'aria-label'?: string })['aria-label']}
          searchPlaceholder={typeof rest.placeholder === 'string' ? rest.placeholder : undefined}
          searchable={phonePickerSearchable({ searchable: rest.searchable, optionCount, externalSearch: !!rest.onSearchChange })}
          searchValue={rest.searchValue}
          onSearchChange={rest.onSearchChange}
          nothingFoundMessage={rest.nothingFoundMessage}
          renderOption={rest.renderOption ? (item) => rest.renderOption?.({ option: item.option, checked: item.checked }) : undefined}
          contentStyle={modalContentStyle}
          doneLabel={doneLabel}
        />
      </>
    );
  }

  return <MultiSelect {...rest} value={values} onChange={(v) => onChange?.(name, v)} />;
};
